import { computed, defineComponent, h, ref, watch } from 'vue';
import { box, column, indexView, loader, pagination, tag, textColumn } from '@/ui/components';
import { filterFormLoader } from '@/ui/components/Loader';
import { assigneeFilter, dateRangeFilter, searchFilter, entityItemFilter } from '@/ui/components/filters/filters-horizontal';
import { bulkDelete, list as getInspectionList, remove } from '@/services/api/resources/inspection';
import { InspectionList, InspectionRecord } from '@/entities/inspection';
import { d, t } from '@/ui/i18n/i18n';
import { tagColumn } from '@/ui/components/table/TableColumn';
import { dataNameProp, filterListToOptions, sequence } from '@/ui/helpers';
import { SelectOptions, SortDirection } from '@/ui/types';
import * as qs from '@/ui/queryState';
import { eventBus } from '@/_legacy/eventBus';
import { Events } from '@/ui/events';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as getAllEntityItems } from '@/services/api/resources/entityItem';
import { emptyList } from '@/services/api';
import { InspectionStatus, Status } from '@/entities/todo';
import { RouteName } from '@/ui/routes';
import { useInspectionCheck } from '@/ui/pages/inspection/useInspectionCheck';
import InspectionOverdueEmpty from '@/ui/pages/inspection/InspectionOverdueEmpty.vue';
import InspectionDueEmpty from '@/ui/pages/inspection/InspectionDueEmpty.vue';
import { bulkDeleteButton } from '@/ui/components/Button';
import { useBulkDeletable } from '@/ui/pages/useBulkDeletable';
import { BulkDeletableTable } from '@/ui/components/table/BulkDeletableTable';
import { FormsTabNames } from '@/ui/components/navigation/constants';
import { user } from '@/ui/auth/auth';

export const InspectionPendingTab = defineComponent({
  name: 'InspectionPendingTab',
  props: ['activeTab'],
  setup(props) {
    const initialLoading = ref(true);

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir, onSortChange } = useSorting('dueAt', SortDirection.Ascending);
    const { checkResult, performCheck } = useInspectionCheck([Status.InProgress, Status.Pending]);

    const search = qs.string('q', '');
    const assignee = qs.numbers('assignee', []);
    const entityItem = qs.numbers('entityItem', []);
    const start = qs.nullableDate('start', null);
    const end = qs.nullableDate('end', null);
    const categories = qs.numbers('category', []);
    const questionTitle = qs.strings('questionTitle', []);
    const response = qs.strings('response', []);

    // get filters task
    const users = ref<SelectOptions>([]);
    const entityItems = ref<SelectOptions>([]);
    const getFilters = useTask(
      () => sequence(getAllUsers(), getAllEntityItems()),
      ([userList, entityItemList]) => {
        users.value = filterListToOptions(userList);
        entityItems.value = filterListToOptions(entityItemList);
      },
    );

    const isOverdueTab = computed(() => props.activeTab === FormsTabNames.OVERDUE);

    // get list task
    const list = ref<InspectionList>(emptyList());
    const getList = useTask(
      () => getInspectionList({
        timezone: user.value!.timezone,
        page: page.value,
        perPage: perPage.value,
        search: search.value,
        assignee: assignee.value,
        entityItem: entityItem.value,
        status: [Status.Pending],
        inspectionStatus: [
          InspectionStatus.Empty,
          InspectionStatus.Done,
          InspectionStatus.Archived,
          InspectionStatus.CantDo,
        ],
        dueAtStart: start.value,
        dueAtEnd: end.value,
        categories: categories.value,
        sort: sort.value,
        dir: dir.value,
        overdue: isOverdueTab.value,
        questionTitle: questionTitle.value,
        response: response.value,
        doneBy: [],
      }),
      listResponse => {
        list.value = listResponse;
      },
    );

    watch([
      page,
      perPage,
      search,
      assignee,
      entityItem,
      response,
      questionTitle,
      start,
      end,
      sort,
      dir,
      isOverdueTab,
    ], getList.run);

    watch(() => getList.state.value.pending, newVal => {
      initialLoading.value = newVal;
    });

    const redirectRoute = ({ row }: { row: InspectionRecord }): RouteName =>
      [Status.Pending, Status.InProgress].includes(row.data.status) ? 'inspectionEdit' : 'inspectionDetail';

    const {
      selectedResources,
      deleteBulkTask,
      onCheckedRowsUpdate,
      isBulkDeletionDisabled,
    } = useBulkDeletable<InspectionRecord>({
      getList,
      bulkDeleteFn: bulkDelete,
      mapIds: record => record.data.inspection.id,
      // eslint-disable-next-line max-len
      onDeleteFilterFn: inspection => (currentElement => currentElement.data.inspection.id !== inspection.data.inspection.id),
    });

    return function render() {
      return loader('div', performCheck, [
        checkResult.value ? indexView(
          [
            filterFormLoader(getFilters, [
              bulkDeleteButton({ resources: selectedResources, disabled: isBulkDeletionDisabled }),
              searchFilter(search, resetPagination),
              dateRangeFilter(start, end, resetPagination, 12, t('common.deadline')),
              assigneeFilter(assignee, users.value, resetPagination),
              entityItemFilter(entityItem, entityItems.value, resetPagination),
            ]),
          ],
          [
            box([
              loader('div', getList, [
                h(BulkDeletableTable, {
                  props: {
                    permissionName: 'update',
                    editRoute: redirectRoute,
                    entityIdPath: ['data', 'inspection', 'id'],
                    list: list.value.data,
                    deleteFn: remove,
                    sort: sort.value,
                    dir: dir.value,
                    loading: deleteBulkTask.state.value.pending,
                    selectedRecords: selectedResources.value,
                    paginationLinks: list.value.meta,
                    isRowCheckable: (row: BaseEntity) => row.authorizedTo.delete,
                    recordName: {
                      plural: t('inspections.scheduled'),
                      singular: t('common.scheduled') + t('common.inspection'),
                    },
                  },
                  scopedSlots: {
                    default: () => [
                      column(
                        {
                          field: 'title',
                          label: t('common.title'),
                          sortable: true,
                        }, {
                          default: ({ row: inspection }: { row: InspectionRecord }) => inspection.data.title,
                        },
                      ),
                      textColumn(
                        {
                          field: 'dueAt',
                          label: t('common.deadline'),
                          cellClass: 'deadline-column',
                          sortable: true,
                        }, {
                          default: ({ row: inspection }: { row: InspectionRecord }) => inspection.data.dueAt
                            ? h('span', d(inspection.data.dueAt))
                            : [tag('neutral', t('common.no-deadline'))],
                        },
                      ),
                      tagColumn({
                        field: 'assignees',
                        label: t('inspection-report.assignee'),
                      }, {
                        default: ({ row: inspection }: { row: InspectionRecord }) =>
                          h('div', [
                            ...inspection.data.inspection.assignedGroups.map(i => i.data.title),
                            ...inspection.data.assignees.map(dataNameProp),
                          ].join(', ')),
                      }),
                      textColumn(
                        {
                          field: 'entityItem',
                          label: t('common.entity-item'),
                          sortable: true,
                        }, {
                          default: ({ row: inspection }: { row: InspectionRecord }) => inspection.data.entityItem
                            ? inspection.data.entityItem.data.title
                            : null,
                        },
                      ),
                    ],
                  },
                  on: {
                    sort: (field: string, direction: SortDirection) => qs.updateMultiple(() => {
                      onSortChange(field, direction);
                      resetPagination();
                    }),
                    deleted: (id: number) => eventBus.$emit(Events.inspectionDeleted, { id }),
                    'update:checkedRows': onCheckedRowsUpdate,
                  },
                }),
                pagination({
                  current: page.value,
                  total: list.value.meta.total,
                  perPage: perPage.value,
                }),
              ], !initialLoading.value),
            ]),
          ],
        ) : box([h(isOverdueTab.value ? InspectionOverdueEmpty : InspectionDueEmpty)]),
      ], false);
    };
  },
});
